// Preview-bypass detection — extracted from FeatureSection so the
// PreviewBanner can also read the bypass state.
//
// Trigger model (unchanged from the original FeatureSection
// implementation):
//
//   /?preview=1 → set localStorage[PREVIEW_KEY] = '1' and return true
//   /?preview=0 → remove localStorage[PREVIEW_KEY] and return false
//   no param    → return localStorage[PREVIEW_KEY] === '1'
//
// Reads only — never crosses the browser boundary, never PUTs the DB.
// Safe to call from any component that needs to know whether the
// editor is in flag-bypass mode.

import { useEffect, useState } from 'react';

export const PREVIEW_BYPASS_KEY = 'lm-preview-bypass';

export function usePreviewBypass(): boolean {
  const [enabled, setEnabled] = useState(() => readBypass());
  // Re-check on history navigation so toggling via URL is reflected
  // without a full page reload (matches the original FeatureSection
  // behaviour exactly).
  useEffect(() => {
    function onPop() {
      setEnabled(readBypass());
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  return enabled;
}

function readBypass(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    const param = params.get('preview');
    if (param === '1') {
      try { window.localStorage.setItem(PREVIEW_BYPASS_KEY, '1'); } catch { /* ignore */ }
      return true;
    }
    if (param === '0') {
      try { window.localStorage.removeItem(PREVIEW_BYPASS_KEY); } catch { /* ignore */ }
      return false;
    }
    return window.localStorage.getItem(PREVIEW_BYPASS_KEY) === '1';
  } catch {
    return false;
  }
}

/** Clear the bypass programmatically — used by the PreviewBanner's
 *  "Clear" affordance. Returns true if the bypass was set and is now
 *  cleared. */
export function clearPreviewBypass(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const had = window.localStorage.getItem(PREVIEW_BYPASS_KEY) === '1';
    window.localStorage.removeItem(PREVIEW_BYPASS_KEY);
    return had;
  } catch {
    return false;
  }
}
